<script setup lang="ts">
import { computed, toRefs } from 'vue';
import { RRule } from 'rrule';
import download from 'downloadjs';
import { useRouter } from 'vue-router';
import { RSMService } from '@/services';
import { can, useDateTime } from '@/composables';
import { ACTIVITY_LOG_IMPORT, PERMISSIONS, SUBJECT_TYPE, STATUS_IMPORT_RSM, ACTIVITY_EVENT_TYPE } from '@/enum';
import { useDocumentsStore, useCalendarStore } from '@/store';

interface LogItemPropsInterface {
  accessTenant?: {
    full_name_user: string;
    id: number;
    tenant_id: number;
    tenant_name: string;
  };
  impersonatedBy?: {
    id: number;
    full_name: string;
  };
  causer: {
    id: number;
    full_name: string;
    therapy_event_type: string;
  };
  subject: {
    id: number;
    full_name?: string;
    name?: string;
    therapy_event_type?: string;
    start_at?: string;
    treatment_goal_type_id?: string;
    student?: {
      id: number;
      full_name: string;
    };
    event_display?: string;
    acronym?: string;
    processed_at?: string;
    status?: string;
    error_file?: string;
    file?: string;
  };
  subjectType: string;
  timeAgo: string;
  changes?: string;
  event: string;
  custom: {
    description: string;
    icon: string;
  };
  description?: string;
  properties: {
    changes: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    new_values: any;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    old_values: any;
    request?: { event_date: string; notes: string; therapy_result_type_id: number; therapy_event_ids: number[] };
    path: string;
    tab: string;
  };
}
const props = defineProps<LogItemPropsInterface>();
const {
  impersonatedBy,
  event,
  causer,
  subject,
  subjectType,
  custom,
  timeAgo,
  changes,
  properties,
  accessTenant,
  description,
} = toRefs(props);
const documentStore = useDocumentsStore();
const subjectIsEvent = () => subjectType.value === SUBJECT_TYPE.THERAPY_EVENT;
const subjectIsTherapyResult = () => event.value === ACTIVITY_EVENT_TYPE.THERAPY_RESULT;
const subjectIsBulkAttendance = () => event.value === ACTIVITY_EVENT_TYPE.BULK_ATTENDANCE;
const subjectIsImpersonate = () => event.value === ACTIVITY_EVENT_TYPE.IMPERSONATE;
const subjectIsTreatmentGoal = () => subjectType.value === SUBJECT_TYPE.TREATMENT_GOAL;
const subjectIsOrganization = () => subjectType.value === SUBJECT_TYPE.ORGANIZATION;
const subjectIsRsmImport = () => subjectType.value === SUBJECT_TYPE.FILE_IMPORT;
const isImport = computed<boolean>(() => event.value === ACTIVITY_LOG_IMPORT.EVENT);
const route = useRouter();
const calendarStore = useCalendarStore();

/**
 * set the bulk import text with no hyperlink
 */
const bulkImportText = () => {
  if (subjectIsBulkAttendance())
    return properties.value.request ? properties.value.request.event_date : subject.value.event_display;
};

/**
 * set the main text based on start/end date and subject values
 */
const subjectText = () => {
  if (event.value === SUBJECT_TYPE.LOGIN || subjectIsBulkAttendance()) return '';

  if (subjectIsEvent() || subjectIsTherapyResult()) {
    const startTime = new Intl.DateTimeFormat('default', {
      hour: 'numeric',
      minute: 'numeric',
    }).format(useDateTime().fromUTCStringToLocalDate(subject.value.start_at as string));

    const eventDate = properties.value?.request?.event_date || subject.value.event_display;
    return `${eventDate} at ${startTime.toUpperCase().replace(' ', '')}`;
  }

  if (subjectIsTreatmentGoal()) return subject.value.student?.full_name;
  if (subjectIsOrganization()) return subject.value.name;
  if (subjectIsImpersonate()) return subject.value.full_name;
  if (subjectIsRsmImport()) {
    const processed_at = subject.value.processed_at;
    const subjectName = subject.value.file;
    const nameParts = subjectName.split('/');
    // fallbackName assumes that the array has a length of 4
    const fallbackName = nameParts[nameParts.length - 1] || 'unknown';

    return `rsm-import-${processed_at ? processed_at.split(' ')[0] + '.csv' : fallbackName}`;
  }
};

const evaluateStatusToImport = () => {
  switch (subject.value.status) {
    case STATUS_IMPORT_RSM.ERRORS:
      return ' with some errors - ';
    case STATUS_IMPORT_RSM.COMPLETED:
      return ' with no errors';
    case STATUS_IMPORT_RSM.PROCESSING:
      return ' processing';
    default:
      return ' pending to import';
  }
};

/**
 * find the start at , end at, or duration in string changes
 * @param str
 */
const parseStartEnd = (str: string, searchString: string) => {
  const split = str.split(',');
  let returnStr = '';
  split.forEach((txt) => {
    if (txt.match(searchString)) {
      returnStr = txt.trim();
      const returnFromDate = txt
        .substring(txt.indexOf(searchString) + (searchString.length + 1), searchString.length + 21)
        .trim();

      const returnToDate = txt.substring(returnStr.indexOf('to') + 3, returnStr.indexOf('to') + 23).trim();
      returnStr = `${searchString} ${splitStringIntoReadableDate(returnFromDate)} to ${splitStringIntoReadableDate(
        returnToDate,
      )} `;
    }
  });

  return returnStr;
};

const parseDuration = (str: string) => str.substring(str.indexOf('duration from', 25)).trim();

/**
 * split the unreadable UNTIL=yyyymmdd z string into a formatted date
 */
const splitStringIntoReadableDate = (dateStr: string) => {
  const dateArr = dateStr.split('-');

  const yyyy = dateArr[0];
  const mm = dateArr[1];
  //now split day time
  const dayTime = dateArr[2].split(' ');
  const dd = dayTime[0];
  //get the parsed time and format it
  const time = dayTime[1] ? useDateTime().formatAMPMTime(dateStr) : '';

  return ` ${mm}/${dd}/${yyyy} ${time}`;
};

/**
 * format the RRULE 'changes' string into something more readable
 * for example:
 * original string: 'from DTSTART:20230105T230000Z RRULE:FREQ=WEEKLY;UNTIL=20230106T010000Z to '
 * converts to:  'from 01/05/2023 to 01/05/2023 frequency=WEEKLY, until 01/06/2023'
 */
const formatChanges = (txt: string): string => {
  let startAt, endAt, freq, rruleObj;
  if (txt.includes('RRULE')) {
    const split = txt.split(';');
    split.forEach((rule) => {
      if (rule.toUpperCase().match('UNTIL=')) {
        rruleObj = RRule.parseString(rule.substring(rule.indexOf('UNTIL='), rule.indexOf('Z') + 1).trim());
        endAt = ` end at ${useDateTime().formatLocalDateTime(rruleObj.until, true, true)}`;
      }

      if (rule.toUpperCase().match('DTSTART')) {
        rruleObj = RRule.parseString(rule.substring(rule.indexOf('DTSTART')).trim());
        startAt = ` start at ${useDateTime().formatLocalDateTime(rruleObj.dtstart, true, true)}`;
      }

      if (rule.toUpperCase().match('FREQ')) {
        rruleObj = rule.substring(rule.indexOf('FREQ=') + 5).trim();
        freq = ` frequency ${rruleObj}`;
      }
    });
  } else {
    //else it is NOT an RRULE event just a single day which is formatted diff and freq is duration
    startAt = txt.includes('start at from') ? parseStartEnd(txt, 'start at from') : '';
    endAt = txt.includes('end at from') ? parseStartEnd(txt, 'end at from') : '';
    freq = txt.includes('duration from') ? parseDuration(txt) : '';
  }
  return `${startAt}, ${endAt} ${freq}`;
};

const routeToRedirect = async () => {
  if (subjectIsImpersonate()) return route.replace(`/user/${subject.value.id}`);

  if (subjectIsEvent() || subjectIsTherapyResult()) {
    calendarStore.redirectedToDate = useDateTime().fromUTCStringToLocalDate(subject.value.start_at as string);
    calendarStore.evenToOpen = subject.value.id;
    return route.replace(`/calendar/day`);
  }

  if (subjectIsTreatmentGoal()) return route.replace(`/user/${subject.value.student?.id}`);
  if (subjectIsOrganization()) return route.replace(`/organizations/${subject.value.id}`);

  return route.replace(`/user/${subject.value.id}`);
};

const downloadRSMFile = async (downloadRsmOrError: boolean) => {
  const path = downloadRsmOrError ? subject.value.file : subject.value.error_file;
  const nameFile = downloadRsmOrError ? subject.value.file : subject.value.error_file;

  if (!path) return;

  const response = await RSMService.dowloadRsmFile(path);
  const content = response.headers['content-type'];

  download(response.data, nameFile, content);
};

const getCustomDescription = (description: string, name: string) => {
  if (description.match('detached') || description.match('attached') || description.match('send a reset'))
    return `${description} ${name}`;
  else return description;
};

const onDownloadDocument = (id: number, name: string) => documentStore.downloadDocument(id, name);
</script>

<template>
  <div class="flex w-full mb-3">
    <Icon :name="custom.icon" class="text-600 text-base text-gray-500" />
    <div class="ml-2 flex flex-column">
      <div class="align-items-center inline-block text-sm">
        <p v-if="accessTenant && !impersonatedBy" class="dark-light inline-block">
          <span v-text="accessTenant.full_name_user" />
          ( as
          <RouterLink
            v-if="can([PERMISSIONS.DASHBOARD_ACTIVITY_DATE_CLICK])"
            :to="`/user/${causer.id}`"
            class="font-bold"
            v-text="causer.full_name"
          />
          <span v-else v-text="causer.full_name" />
          )
        </p>
        <p v-if="accessTenant && impersonatedBy" class="dark-light inline-block">
          <span v-text="accessTenant.full_name_user" />
          ( as
          <RouterLink :to="`/user/${causer.id}`" class="font-bold" v-text="causer.full_name" />
          )
        </p>
        <p
          v-if="!!!accessTenant && impersonatedBy && can([PERMISSIONS.DASHBOARD_ACTIVITY_DATE_CLICK])"
          class="dark-light inline-block"
        >
          <RouterLink :to="`/user/${impersonatedBy.id}`" class="font-bold" v-text="impersonatedBy.full_name" />
          ( as
          <RouterLink :to="`/user/${causer.id}`" class="font-bold" v-text="causer.full_name" />
          )
        </p>
        <p
          v-if="!!!accessTenant && impersonatedBy && !can([PERMISSIONS.DASHBOARD_ACTIVITY_DATE_CLICK])"
          class="dark-light inline-block"
        >
          <span class="font-semibold text-teal-500" v-text="impersonatedBy.full_name" />
          ( as
          <span class="font-semibold text-teal-500" v-text="causer.full_name" />
          )
        </p>
        <span v-if="!causer.id" class="font-semibold text-teal-500" v-text="causer.full_name" />
        <RouterLink
          v-if="!accessTenant && !impersonatedBy && causer.id && can([PERMISSIONS.DASHBOARD_ACTIVITY_DATE_CLICK])"
          :to="`/user/${causer.id}`"
          class="font-bold"
          v-text="causer.full_name"
        />
        <span
          v-else-if="!impersonatedBy && causer.id && !can([PERMISSIONS.DASHBOARD_ACTIVITY_DATE_CLICK])"
          class="font-semibold text-teal-500"
          v-text="causer.full_name"
        />
        <div v-if="isImport" class="inline">
          <span class="mx-1 dark-light" v-text="ACTIVITY_LOG_IMPORT.LABEL" />
          <a
            href="#"
            class="font-bold"
            @click="onDownloadDocument(properties.new_values.success.id, properties.new_values.success.name)"
            v-text="properties.new_values.success.name"
          />
        </div>
        <span class="mx-1 dark-light" v-text="getCustomDescription(custom.description, subject.full_name)" />
        <span v-if="subjectIsBulkAttendance()" class="dark-light" v-text="bulkImportText()" />
        <a
          v-else-if="!isImport && !subjectIsRsmImport() && !subjectIsImpersonate()"
          class="font-bold"
          @click="routeToRedirect"
          v-text="subjectText()"
        />
        <a
          v-else-if="subjectIsRsmImport()"
          class="font-semibold text-color-green"
          @click="downloadRSMFile(true)"
          v-text="subjectText()"
        />
        <span v-if="subjectIsRsmImport()" class="dark-light" v-text="evaluateStatusToImport()" />
        <a
          v-if="subjectIsRsmImport() && subject.status === STATUS_IMPORT_RSM.ERRORS"
          class="font-bold text-color-red"
          @click="downloadRSMFile(false)"
          v-text="'Download Error Report'"
        />
        <a v-else-if="subjectIsImpersonate()" class="font-bold" @click="routeToRedirect" v-text="subjectText()" />
        <!-- eslint-disable vue/no-v-html -->
        <a
          v-if="isImport && custom.description === ACTIVITY_LOG_IMPORT.FAILURE"
          href="#"
          class="font-bold text-color-red"
          @click="onDownloadDocument(properties.new_values.error.id, properties.new_values.error.name)"
          v-text="'- Download Error Report'"
        />
        <!-- eslint-disable vue/no-v-html -->
        <span
          v-if="!accessTenant && !impersonatedBy && !causer"
          class="dark-light"
          v-html="changes ? ` <strong>${description}</strong> ${formatChanges(changes)}` : ''"
        />
        <span
          v-if="
            [
              ACTIVITY_EVENT_TYPE.PERSON_ARCHIVE,
              ACTIVITY_EVENT_TYPE.PERSON_CREATE,
              ACTIVITY_EVENT_TYPE.PERSON_INFORMATION_CHANGE,
              ACTIVITY_EVENT_TYPE.PERSON_UNARCHIVE,
            ].includes(event)
          "
          class="text-teal-500"
          v-text="subject.full_name"
        />
      </div>
      <small v-text="timeAgo" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.dark-light {
  color: $grayDark;
}
</style>
